import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriptionStatus = 'trialing' | 'active' | 'cancelled' | 'past_due' | null;
export type SubscriptionPlan   = 'junior'   | 'lead'   | null;

export interface UseSubscriptionReturn {
  status:             SubscriptionStatus;
  plan:               SubscriptionPlan;
  trialEndsAt:        Date | null;
  /** Math.ceil((trialEndsAt - now) / 86400000). null when not actively trialing. */
  daysUntilTrialEnd:  number | null;
  /** status === 'trialing' AND trial_ends_at < now */
  isTrialExpired:     boolean;
  /** status === 'trialing' AND NOT expired */
  isTrialing:         boolean;
  /** plan === 'lead' OR isTrialing */
  hasLeadAccess:      boolean;
  loading:            boolean;
  /** Re-fetches subscription and returns the fresh status value. */
  refresh:            () => Promise<SubscriptionStatus>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSubscription(): UseSubscriptionReturn {
  const { user } = useAuth();
  const [status,      setStatus]      = useState<SubscriptionStatus>(null);
  const [plan,        setPlan]        = useState<SubscriptionPlan>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [loading,     setLoading]     = useState(true);

  const fetchSubscription = useCallback(async (): Promise<SubscriptionStatus> => {
    if (!user) { setLoading(false); return null; }

    const { data, error } = await supabase
      .from('subscriptions_safe')
      .select('status, plan, trial_ends_at')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      Sentry.captureException(error, { tags: { hook: 'useSubscription' }, extra: { userId: user.id } });
    }
    if (data) {
      setStatus((data.status      as SubscriptionStatus) ?? null);
      setPlan  ((data.plan        as SubscriptionPlan)   ?? null);
      setTrialEndsAt(data.trial_ends_at ? new Date(data.trial_ends_at) : null);
    }
    setLoading(false);
    return (data?.status as SubscriptionStatus) ?? null;
  }, [user]);

  // Fetch on mount / user change
  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  // Refresh on window focus (tab switch back)
  useEffect(() => {
    window.addEventListener('focus', fetchSubscription);
    return () => window.removeEventListener('focus', fetchSubscription);
  }, [fetchSubscription]);

  // ── Derived values (recomputed each render) ───────────────────────────────
  const now            = new Date();
  const isTrialExpired = status === 'trialing' && trialEndsAt !== null && trialEndsAt < now;
  const isTrialing     = status === 'trialing' && !isTrialExpired;

  const daysUntilTrialEnd = isTrialing && trialEndsAt !== null
    ? Math.ceil((trialEndsAt.getTime() - now.getTime()) / 86400000)
    : null;

  const hasLeadAccess = plan === 'lead' || isTrialing;

  return {
    status, plan, trialEndsAt,
    daysUntilTrialEnd, isTrialExpired, isTrialing, hasLeadAccess,
    loading,
    refresh: fetchSubscription,
  };
}
